import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { formatDateToMMDDYYYY } from '@zg-rentals/date-utils';

import { Alert, Box, Button, Heading, Modal, Toast, useToast } from '@zillow/constellation-10';

import {
  RentalManagerRenterWebApiHandler_update,
  RentalManagerUserInfoWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import { DatarouterError } from '@zg-rentals/web-base';

import TenantForm from '../TenantForm';
import { LEASE_TERM_OPTIONS } from '../FormFieldLeaseTerm';

import type { Client } from '@zg-rentals/http-client';
import type { TenantFormProps } from '../TenantForm';

import type {
  RentalManagerRenterAddOrUpdateRenterDto,
  RentalManagerRenterContactTenantDto,
  RentalManagerRenterUpdateRenterRequestDto,
  RentalManagerRenterUpdateRenterResponseDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import { logError } from '@zg-rentals/log-error';

export type ExtendedRentalManagerRenterContactAddRenterDto = RentalManagerRenterAddOrUpdateRenterDto & {
  renterId: string;
};

export type ModalEditTenantFormProps = {
  client: Client;
  headerTitle?: string;
  isOpen: boolean;
  listingAlias: string;
  primaryCTALabel?: string;
  renter: RentalManagerRenterContactTenantDto;
  onClose: () => void;
  onSuccess?: (updatedRenterDto?: RentalManagerRenterUpdateRenterResponseDto) => void | null;
  onError?: (error: DatarouterError) => void | null;
};

type DropdownSelectLeaseTermData = {
  value: string;
  meta: Record<string, unknown>;
  calls: number;
};

const MODAL_EDIT_TENANT_FORM_CONTENT = {
  HEADER_TITLE: 'Edit tenant',
  PRIMARY_CTA_LABEL: 'Save',
} as const;

const TOAST_MESSAGE = {
  TENANTS_ADDED: 'Tenants added successfully',
  TENANT_EDITED: 'Tenant details edited successfully',
  TENANT_REMOVED: 'The tenant has been successfully removed',
} as const;

const defaultEndDate = '';

const getInitialLeaseTermValue = (endDateMs?: number): string => {
  if (!endDateMs) {
    return LEASE_TERM_OPTIONS[1].value;
  }
  return LEASE_TERM_OPTIONS[0].value;
};

/**
 * @param {ModalEditTenantFormProps} props
 *
 * @param {Client} props.client - The client to use for the query
 * @param {RentalManagerRenterContactTenantDto} props.renter - The renter to edit
 * @param {string} props.headerTitle - The title of the modal
 * @param {string} props.listingAlias - The listing alias to use for the query
 * @param {string} props.primaryCTALabel - The label of the primary CTA button
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {() => void} props.onClose - The function to call when the modal is closed
 * @param {(addedRenters?: Array<RenterInfoRenterAddedDto> | null) => void} props.onSuccess - The function to call when the tenant is successfully edited
 * @param {(error: DatarouterError) => void} props.onError - The function to call when the tenant is not successfully edited
 *
 * @returns {JSX.Element}
 */

const ModalEditTenantForm = ({
  client,
  headerTitle = MODAL_EDIT_TENANT_FORM_CONTENT.HEADER_TITLE,
  isOpen,
  listingAlias,
  primaryCTALabel = MODAL_EDIT_TENANT_FORM_CONTENT.PRIMARY_CTA_LABEL,
  renter,
  onClose,
  onSuccess,
  onError,
}: ModalEditTenantFormProps): JSX.Element => {
  const [startDate, setStartDate] = useState<string>(
    formatDateToMMDDYYYY(new Date(renter?.displayTenancy.startDateMs)),
  );
  const [endDate, setEndDate] = useState<string>(
    renter?.displayTenancy?.endDateMs
      ? formatDateToMMDDYYYY(new Date(renter.displayTenancy.endDateMs))
      : defaultEndDate,
  );

  const [comboboxLeaseTermData, setComboboxLeaseTermData] = useState<DropdownSelectLeaseTermData>({
    value: getInitialLeaseTermValue(renter?.displayTenancy?.endDateMs),
    meta: {},
    calls: 0,
  });

  const [serverError, setServerError] = useState<string | null>(null);

  const [tenantFormList, setTenantFormList] = useState<Record<string, TenantFormProps>>({});

  const { enqueueToast } = useToast();

  const previousEndDateRef = useRef<string>(endDate);

  const getTenantFormValidation = (tenantFormList: Record<string, TenantFormProps>) => {
    const hasFieldError = Object.values(tenantFormList).some((tenantForm) => tenantForm.hasFieldError);

    return hasFieldError;
  };

  const tenantFormListLength = Object.keys(tenantFormList).length || 0;
  const isFixedTermLeaseTerm = comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[0].value;
  const isEndDateRequired = isFixedTermLeaseTerm;

  const isPrimaryCTAButtonDisabled =
    !startDate ||
    (isEndDateRequired && !endDate) ||
    tenantFormListLength === 0 ||
    getTenantFormValidation(tenantFormList) === true;

  useEffect(() => {
    if (isOpen) {
      updateFormData(renter);
      clearServerError();
    }
  }, [isOpen, renter]);

  useEffect(() => {
    // keep a reference to the previous end date
    if (comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[0].value) {
      setEndDate(previousEndDateRef.current);
    }

    // month-to-month lease term
    if (comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[1].value) {
      setEndDate(defaultEndDate);
    }
  }, [comboboxLeaseTermData.value]);

  /**
   * Validate form fields
   */
  useEffect(() => {
    // set the previous end date before validating the form
    if (endDate && endDate !== defaultEndDate) {
      previousEndDateRef.current = endDate;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchUser = async () => {
    const response = await RentalManagerUserInfoWebApiHandler_get({}, client).catch((error) => {
      throw error;
    });

    return response;
  };

  const updateFormData = (renter: RentalManagerRenterContactTenantDto) => {
    setStartDate(formatDateToMMDDYYYY(new Date(renter?.displayTenancy.startDateMs)));

    const newEndDate = renter?.displayTenancy?.endDateMs
      ? formatDateToMMDDYYYY(new Date(renter.displayTenancy.endDateMs))
      : defaultEndDate;
    setEndDate(newEndDate);

    setComboboxLeaseTermData({
      value: getInitialLeaseTermValue(renter?.displayTenancy?.endDateMs),
      meta: {},
      calls: 0,
    });

    replaceTenantInTenantFormList(getFormattedRenter(renter));
  };

  const clearServerError = useCallback(() => {
    setServerError(null);
  }, []);

  const replaceTenantInTenantFormList = (renter: RentalManagerRenterAddOrUpdateRenterDto) => {
    const tenantFormProps: TenantFormProps = {
      id: 1,
      title: 'Personal Info',
      renter,
    };

    setTenantFormList({
      1: tenantFormProps,
    });
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      onClose?.();
    }
  };

  const handleChange = ({ id, value, name }: { id: number; value: string; name: string; hasFieldError?: boolean }) => {
    const copyTenantFormList = { ...tenantFormList };

    // @ts-expect-error
    copyTenantFormList[id]['renter'][name as keyof RentalManagerRenterAddOrUpdateRenterDto] = value;

    setTenantFormList(copyTenantFormList);
  };

  const updateTenantFormListFieldError = ({ id, hasFieldError }: { id: number; hasFieldError: boolean }) => {
    const copyTenantFormList = { ...tenantFormList };

    if (copyTenantFormList[id]) {
      copyTenantFormList[id]['hasFieldError'] = hasFieldError;
    }

    setTenantFormList(copyTenantFormList);
  };

  const getFormattedRenter = (renter: RentalManagerRenterContactTenantDto) => {
    return {
      firstName: renter.firstName,
      lastName: renter.lastName,
      phoneNumber: renter.phoneNumber,
      renterId: renter.renterId,
      rawEmailAddress: renter.emailAddress,
      role: {
        type: 'TENANT',
        roleStartDateMs: renter.displayTenancy.startDateMs,
        roleEndDateMs: renter.displayTenancy.endDateMs,
      },
    } as RentalManagerRenterAddOrUpdateRenterDto;
  };

  const updateRenter = async (postData: RentalManagerRenterUpdateRenterRequestDto) => {
    const response = await RentalManagerRenterWebApiHandler_update(postData, client).catch((error) => {
      throw error;
    });

    return response as RentalManagerRenterUpdateRenterResponseDto;
  };

  const handlePrimaryCTAClick = async () => {
    const response = await fetchUser();

    const { userToken = null } = response ?? {};

    const formattedData: RentalManagerRenterUpdateRenterRequestDto = {
      listingAlias,
      referenceId: userToken ? `zillow_${userToken}` : 'zillow_0000000000',
      referenceIdType: 'manualEntryId',
      renterToUpdateDto: {
        firstName: tenantFormList[1].renter.firstName,
        lastName: tenantFormList[1].renter.lastName,
        phoneNumber: tenantFormList[1].renter.phoneNumber,
        // force rawEmailAddress to be lowercase
        // BE is requiring this normalization be done on the FE
        rawEmailAddress: tenantFormList[1].renter.rawEmailAddress?.toLowerCase(),
        renterId: tenantFormList[1].renter.renterId,
      },
    };

    clearServerError();

    updateRenter(formattedData).then(handleEditRentersSuccess).catch(handleEditRentersError);
  };

  const handleEditRentersSuccess = (response: RentalManagerRenterUpdateRenterResponseDto) => {
    onSuccess?.(response);
    handleClose(false);
    enqueueToast(<Toast tone="success">{TOAST_MESSAGE.TENANTS_ADDED}</Toast>);

    return response;
  };

  const handleEditRentersError = (error: DatarouterError) => {
    const displayError = DatarouterError.getDisplayMessage(error);
    let errorMessage = displayError;

    // displayError might only be a string that can not be converted via JSON.parse
    try {
      const parsed = JSON.parse(displayError);

      if (parsed?.error?.message) {
        errorMessage = parsed.error.message;
      }
    } catch {
      // displayError is not JSON, use as is
    }

    const capitalizedError =
      typeof errorMessage === 'string' && errorMessage.length > 0
        ? errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1)
        : 'An unexpected error occurred';

    setServerError(capitalizedError);
    onError?.(new DatarouterError(capitalizedError, 'EDIT_RENTERS_ERROR', 500));

    logError({
      error: new Error(`Error editing tenants: ${capitalizedError}`),
      errorType: 'EditRentersError',
      context: 'ModalEditTenantForm@handleEditRentersError',
    });
  };

  const renderBody = () => {
    return (
      <Box data-testid="ModalEditTenantForm">
        {serverError && (
          <>
            <Box
              data-testid="ErrorContainer"
              css={{
                marginBottom: 'loose',
              }}
            >
              <Alert tone="warning">{serverError}</Alert>
            </Box>
          </>
        )}

        <Box data-testid="TenantFormContainer">
          {Object.entries(tenantFormList).length > 0 && (
            <Box
              css={{
                marginBottom: 'tight',
              }}
            />
          )}

          {Object.entries(tenantFormList).map(([key, tenantFormProps]) => {
            return (
              <Box key={key}>
                <TenantForm
                  {...tenantFormProps}
                  isEditMode={true}
                  title="Personal Info"
                  onChange={handleChange}
                  onFieldError={updateTenantFormListFieldError}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Modal
        open={isOpen}
        size={'md'}
        scrollDocument={false}
        header={<Heading level={2}>{headerTitle}</Heading>}
        body={renderBody()}
        footer={
          <Button tone={'brand'} disabled={isPrimaryCTAButtonDisabled} onClick={handlePrimaryCTAClick}>
            {primaryCTALabel}
          </Button>
        }
        onOpenChange={handleClose}
      />
    </>
  );
};

export default memo(ModalEditTenantForm);
